import api, { ApiResponse } from '.'
import {
  CreateThreePartiesParams,
  CreateTwoPartiesParams,
} from '../../../backend/src/services/resources/period-summaries/period-summaries.controller.service.types'
import {
  PeriodSummary as PeriodSummarySchema,
  SimulatedPeriodSummary,
} from '../../../backend/src/services/resources/period-summaries/period-summary.types'
import { SimulatePeriodSummaryResponse as SimulateThreePartiesPeriodSummaryResponse } from '../../../backend/src/services/resources/period-summaries/three-parties.period-summaries.business.service.types'
import { Employer } from './organizations'
import { InterimAgency } from './organizations'
import { User } from './users'

const POPULATE = ['employer', 'interimAgency', 'createdBy']

// Types pour les period summaries
export interface PeriodSummary
  extends Omit<PeriodSummarySchema, '_id' | 'employer' | 'interimAgency' | 'createdBy'> {
  _id: string
  employer: Employer
  interimAgency: InterimAgency
  createdBy: User
}

export interface TwoPartiesPeriodSummary extends PeriodSummary {
  type: 'two-parties'
}

export interface ThreePartiesPeriodSummary extends PeriodSummary {
  type: 'three-parties'
}

// Paramètres pour fetch
export interface FetchPeriodSummariesParams {
  start?: Date
  end?: Date
  type?: 'two-parties' | 'three-parties'
  [key: string]: any
}

/**
 * Récupère la liste des period summaries avec filtres optionnels
 */
export const fetch = async (
  params?: Partial<FetchPeriodSummariesParams>,
): Promise<ApiResponse<PeriodSummary[]>> => {
  if (params?.invoiceRef?.length === 1 && params.invoiceRef[0]) {
    params.invoiceRef = ''
  } else if (params?.invoiceRef?.length === 1 && !params.invoiceRef[0]) {
    params['!invoiceRef'] = ''
  } else if (params?.invoiceRef) params.invoiceRef = undefined

  return await api({
    endpoint: '/period-summaries',
    method: 'get',
    params: {
      populate: POPULATE,
      ...(params || {}),
    },
  })
}

/**
 * Récupère un period summary par son ID
 */
export const get = async (id: string): Promise<ApiResponse<PeriodSummary>> =>
  await api({
    endpoint: `/period-summaries/${id}`,
    method: 'get',
    params: {
      populate: POPULATE,
    },
  })

export type SimulatePeriodSummary = SimulateThreePartiesPeriodSummaryResponse

export const simulateThreeParties = async (
  params: CreateThreePartiesParams,
): Promise<ApiResponse<SimulatePeriodSummary>> =>
  await api({
    endpoint: '/period-summaries/three-parties/simulate',
    method: 'get',
    params,
  })

export type SimulateTwoPartiesPeriodSummary = SimulatedPeriodSummary

export const simulateTwoParties = async (
  params: CreateTwoPartiesParams,
): Promise<ApiResponse<SimulateTwoPartiesPeriodSummary>> =>
  await api({
    endpoint: '/period-summaries/two-parties/simulate',
    method: 'get',
    params,
  })

/**
 * Crée un period summary à deux parties
 */
export const createTwoParties = async (
  params: CreateTwoPartiesParams,
): Promise<ApiResponse<TwoPartiesPeriodSummary>> =>
  await api({
    endpoint: '/period-summaries/two-parties',
    method: 'post',
    params,
  })

/**
 * Crée un period summary à trois parties
 */
export const createThreeParties = async (
  params: CreateThreePartiesParams,
): Promise<ApiResponse<ThreePartiesPeriodSummary>> =>
  await api({
    endpoint: '/period-summaries/three-parties',
    method: 'post',
    params,
  })

export type PeriodSummaryUpdatePayload = { invoiceRef?: string | null; id: string }
export const update = async (
  data: PeriodSummaryUpdatePayload,
): Promise<ApiResponse<PeriodSummary>> => {
  const { id, ...params } = data
  return await api({
    endpoint: `/period-summaries/${id}`,
    method: 'put',
    params,
  })
}
